import { useCallback, useEffect, useState } from 'react';
import StatusToast from './StatusToast';
import { adminApi } from './api';

const ROLE_OPTIONS = [
  ['super_admin', 'Super administrateur'],
  ['content_manager', 'Gestionnaire contenu'],
  ['support_agent', 'Support utilisateurs'],
  ['billing_manager', 'Gestionnaire abonnements'],
  ['forum_moderator', 'Modérateur forum'],
];

function roleLabel(role) {
  return ROLE_OPTIONS.find(([value]) => value === role)?.[1] || role || '—';
}

const STATUS_OPTIONS = [
  ['active', 'Actif'],
  ['suspended', 'Suspendu'],
  ['disabled', 'Désactivé'],
];

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('fr-FR') : '—';
}

export function InvitationAcceptScreen({ token, onDone }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (password.length < 12) {
      setError('Le mot de passe doit contenir au moins 12 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    try {
      setBusy(true);
      await adminApi.acceptOperatorInvitation(token, password);
      setDone(true);
      window.history.replaceState({}, '', window.location.pathname);
    } catch (acceptError) {
      setError(acceptError.message || 'Invitation invalide.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="bo-login-page">
      <section className="bo-login-card">
        <div className="bo-brand-mark">CB</div>
        <p className="bo-eyebrow">CONCOURSBf</p>
        <h1>Invitation administration</h1>
        {done ? (
          <>
            <div className="bo-alert bo-alert--success">
              Votre compte back-office est créé.
            </div>
            <button className="bo-button bo-button--primary" onClick={onDone}>
              Aller à la connexion
            </button>
          </>
        ) : (
          <form className="bo-form bo-login-form" onSubmit={submit}>
            <p className="bo-muted">
              Définissez votre mot de passe personnel. Cette invitation est à
              usage unique.
            </p>
            <label>
              Mot de passe
              <input
                className="bo-input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
            <label>
              Confirmer le mot de passe
              <input
                className="bo-input"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
              />
            </label>
            {error && <div className="bo-alert bo-alert--error">{error}</div>}
            <button className="bo-button bo-button--primary" disabled={busy}>
              {busy ? 'Création…' : 'Créer mon compte'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

export default function AdminOperators({ currentAdmin, onUnauthorized }) {
  const [data, setData] = useState({ admins: [], invites: [] });
  const [form, setForm] = useState({
    email: '',
    pseudonym: '',
    firstName: '',
    lastName: '',
    role: 'content_manager',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      setData(await adminApi.operators());
    } catch (loadError) {
      if (loadError.status === 401) onUnauthorized?.();
      else setError(loadError.message || 'Chargement impossible.');
    }
  }, [onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  async function invite(event) {
    event.preventDefault();
    try {
      setBusy(true);
      setError('');
      setNotice('');
      await adminApi.inviteOperator(form);
      setNotice('Invitation envoyée.');
      setForm({
        email: '',
        pseudonym: '',
        firstName: '',
        lastName: '',
        role: 'content_manager',
      });
      await load();
    } catch (inviteError) {
      setError(inviteError.message || 'Invitation impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function updateOperator(admin, patch) {
    try {
      setError('');
      await adminApi.updateOperator(admin.id, patch);
      setNotice('Compte administrateur mis à jour.');
      await load();
    } catch (updateError) {
      setError(updateError.message || 'Modification impossible.');
    }
  }

  return (
    <div className="bo-stack">
      <header>
        <p className="bo-eyebrow">Sécurité</p>
        <h2>Administrateurs</h2>
        <p className="bo-muted">
          Comptes strictement séparés des candidats. Aucun compte administrateur
          n’est créé depuis le front-office.
        </p>
      </header>

      {error && <div className="bo-alert bo-alert--error">{error}</div>}
      <StatusToast message={notice} />

      <section className="bo-panel">
        <h3>Inviter un gestionnaire</h3>
        <form className="bo-form" onSubmit={invite}>
          <label>
            E-mail
            <input
              className="bo-input"
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, email: event.target.value }))
              }
              required
            />
          </label>
          <label>
            Pseudonyme
            <input
              className="bo-input"
              value={form.pseudonym}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  pseudonym: event.target.value,
                }))
              }
              required
            />
          </label>
          <label>
            Prénom
            <input
              className="bo-input"
              value={form.firstName}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  firstName: event.target.value,
                }))
              }
            />
          </label>
          <label>
            Nom
            <input
              className="bo-input"
              value={form.lastName}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  lastName: event.target.value,
                }))
              }
            />
          </label>
          <label>
            Rôle
            <select
              className="bo-input"
              value={form.role}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, role: event.target.value }))
              }
            >
              {ROLE_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button className="bo-button bo-button--primary" disabled={busy}>
            {busy ? 'Envoi…' : 'Envoyer l’invitation'}
          </button>
        </form>
      </section>

      <section className="bo-panel">
        <h3>Comptes back-office</h3>
        <div className="bo-table-wrap">
          <table className="bo-table">
            <thead>
              <tr>
                <th>Gestionnaire</th>
                <th>Rôle</th>
                <th>Statut</th>
                <th>Dernière connexion</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.admins || []).map((admin) => {
                const isSelf = String(admin.id) === String(currentAdmin.id);
                return (
                  <tr key={admin.id}>
                    <td>
                      <strong>
                        {[admin.firstName, admin.lastName].filter(Boolean).join(' ') ||
                          admin.pseudonym}
                      </strong>
                      <small>{admin.email}</small>
                    </td>
                    <td>
                      <select
                        className="bo-input bo-select"
                        value={admin.role}
                        disabled={isSelf}
                        onChange={(event) =>
                          updateOperator(admin, { role: event.target.value })
                        }
                      >
                        {ROLE_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="bo-input bo-select"
                        value={admin.status}
                        disabled={isSelf}
                        onChange={(event) =>
                          updateOperator(admin, { status: event.target.value })
                        }
                      >
                        {STATUS_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{formatDate(admin.lastLoginAt)}</td>
                    <td>
                      <button
                        className="bo-button bo-button--ghost"
                        onClick={async () => {
                          if (
                            !window.confirm(
                              'Révoquer toutes les sessions de ce gestionnaire ?'
                            )
                          ) {
                            return;
                          }
                          try {
                            await adminApi.revokeOperatorSessions(admin.id);
                            setNotice('Sessions administrateur révoquées.');
                            await load();
                          } catch (revokeError) {
                            setError(
                              revokeError.message || 'Révocation impossible.'
                            );
                          }
                        }}
                      >
                        Révoquer sessions
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bo-panel">
        <h3>Invitations récentes</h3>
        <div className="bo-table-wrap">
          <table className="bo-table">
            <thead>
              <tr>
                <th>E-mail</th>
                <th>Rôle</th>
                <th>Expiration</th>
                <th>État</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data.invites || []).map((invite) => {
                const state = invite.usedAt
                  ? 'Utilisée'
                  : invite.revokedAt
                  ? 'Révoquée'
                  : new Date(invite.expiresAt) <= new Date()
                  ? 'Expirée'
                  : 'En attente';
                return (
                  <tr key={invite.id}>
                    <td>{invite.email}</td>
                    <td>{roleLabel(invite.role)}</td>
                    <td>{formatDate(invite.expiresAt)}</td>
                    <td>{state}</td>
                    <td>
                      {state === 'En attente' && (
                        <button
                          className="bo-button bo-button--danger-ghost"
                          onClick={async () => {
                            if (!window.confirm('Révoquer cette invitation ?')) return;
                            try {
                              await adminApi.revokeOperatorInvitation(invite.id);
                              setNotice('Invitation révoquée.');
                              await load();
                            } catch (revokeError) {
                              setError(
                                revokeError.message || 'Révocation impossible.'
                              );
                            }
                          }}
                        >
                          Révoquer
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
